/**
 * Custom hook for Contagem file upload management
 *
 * Manages:
 * - Excel file uploads for Itens da Contagem
 * - Excel file uploads for Informações da Contagem
 * - Upload states (loading, error, result)
 * - File selection state
 *
 * Usage:
 * const { uploading, selectedFile, handleFileSelect, ... } = useContagemUploads();
 */
import { useState, useCallback } from 'react';
import contagemService, { ContagemDadosImportados } from '../services/contagemService';
import { msg } from '../utils/messages';

export interface UseContagemUploadsReturn {
  // Itens upload state
  uploading: boolean;
  downloading: boolean;
  selectedFile: File | null;
  uploadResult: ContagemDadosImportados | null;
  importData: string;

  // Informações upload state
  selectedInformacoesFile: File | null;
  uploadingInformacoes: boolean;

  // Import client/regime selection (Epic 41)
  selectedImportClient: number | null;
  selectedRegime: boolean | null;

  // Actions - Itens
  setSelectedFile: (file: File | null) => void;
  setImportData: (data: string) => void;
  setUploadResult: (result: ContagemDadosImportados | null) => void;
  setUploading: (value: boolean) => void;
  setDownloading: (value: boolean) => void;

  // Actions - Informações
  setSelectedInformacoesFile: (file: File | null) => void;
  setUploadingInformacoes: (value: boolean) => void;

  // Actions - Import selection
  setSelectedImportClient: (clientId: number | null) => void;
  setSelectedRegime: (regime: boolean | null) => void;

  // Upload handlers
  handleUploadItens: (jobId: number, file: File, clienteId?: number, comRegime?: boolean) => Promise<ContagemDadosImportados | null>;
  handleUploadInformacoes: (jobId: number, file: File) => Promise<boolean>;

  // Reset
  resetUploadState: () => void;
}

export const useContagemUploads = (): UseContagemUploadsReturn => {
  // Itens upload state
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<ContagemDadosImportados | null>(null);
  const [importData, setImportData] = useState('');

  // Informações upload state
  const [selectedInformacoesFile, setSelectedInformacoesFile] = useState<File | null>(null);
  const [uploadingInformacoes, setUploadingInformacoes] = useState(false);

  // Import client/regime selection (Epic 41)
  const [selectedImportClient, setSelectedImportClient] = useState<number | null>(null);
  const [selectedRegime, setSelectedRegime] = useState<boolean | null>(null);

  // Upload Itens handler
  const handleUploadItens = useCallback(async (
    jobId: number,
    file: File,
    clienteId?: number,
    comRegime?: boolean
  ): Promise<ContagemDadosImportados | null> => {
    setUploading(true);
    try {
      const result = await contagemService.uploadExcelEpic19(jobId, file, clienteId, comRegime);
      setUploadResult(result);
      msg.success('Arquivo importado com sucesso!');
      return result;
    } catch (err: any) {
      console.error('Erro ao fazer upload:', err);
      // Epic 43: Capturar erros detalhados da resposta 400
      const errorData = err.response?.data || {};
      const errorMessage = errorData.error || err.message || 'Erro ao importar arquivo';
      msg.error(errorMessage);
      // Setar uploadResult com erros para exibir no modal
      if (errorData.erros && errorData.erros.length > 0) {
        setUploadResult({
          total_linhas: 0,
          total_processadas: 0,
          total_erros: errorData.total_erros || errorData.erros.length,
          erros: errorData.erros,
        } as any);
      }
      return null;
    } finally {
      setUploading(false);
      setSelectedFile(null);
    }
  }, []);

  // Upload Informações handler
  // Note: This uses the same uploadExcelEpic19 endpoint for now
  // A dedicated endpoint can be created later if needed
  const handleUploadInformacoes = useCallback(async (
    jobId: number,
    file: File
  ): Promise<boolean> => {
    setUploadingInformacoes(true);
    try {
      // Uses the same upload endpoint - backend handles file type
      await contagemService.uploadExcelEpic19(jobId, file);
      msg.success('Informações importadas com sucesso!');
      return true;
    } catch (err: any) {
      console.error('Erro ao fazer upload de informações:', err);
      msg.error(err.message || 'Erro ao importar informações');
      return false;
    } finally {
      setUploadingInformacoes(false);
      setSelectedInformacoesFile(null);
    }
  }, []);

  // Reset all upload state
  const resetUploadState = useCallback(() => {
    setUploading(false);
    setDownloading(false);
    setSelectedFile(null);
    setUploadResult(null);
    setImportData('');
    setSelectedInformacoesFile(null);
    setUploadingInformacoes(false);
    setSelectedImportClient(null);
    setSelectedRegime(null);
  }, []);

  return {
    // Itens upload state
    uploading,
    downloading,
    selectedFile,
    uploadResult,
    importData,

    // Informações upload state
    selectedInformacoesFile,
    uploadingInformacoes,

    // Import client/regime selection
    selectedImportClient,
    selectedRegime,

    // Actions - Itens
    setSelectedFile,
    setImportData,
    setUploadResult,
    setUploading,
    setDownloading,

    // Actions - Informações
    setSelectedInformacoesFile,
    setUploadingInformacoes,

    // Actions - Import selection
    setSelectedImportClient,
    setSelectedRegime,

    // Upload handlers
    handleUploadItens,
    handleUploadInformacoes,

    // Reset
    resetUploadState,
  };
};

export default useContagemUploads;
